// Icon + label tab strip with the green active underline — the tab bar used
// by module index pages (Financial, packhouse Config, Labour, …). Callers keep
// any role-based filtering of `tabs`, and a wrapper for their border / sticky /
// padding if they want one.
//
// It owns its scrolling strip (packhouse audit PM-SH-07, PW-14). Every caller
// used to hand-roll a scroller with the scrollbar hidden, so on a phone 7 of
// Labour's 10 tabs sat off-screen with nothing saying so, and a caller whose
// parent was a BLOCK (the shared calendar) stacked Month / Week / Agenda
// vertically. So the strip here:
//  - is `flex` whatever the parent is;
//  - fades an edge while there are tabs beyond it, so "there is more" is seen;
//  - keeps the ACTIVE tab on screen — by scrolling the strip itself, never
//    `scrollIntoView`, which would also scroll the page;
//  - uses 44px tabs with role="tablist" / role="tab" / aria-selected.
// ⚠️ A wrapper must not be a scroller itself (`overflow-x-auto`): then IT
// scrolls, and this strip never overflows, so its fades never show.
//
// `wrapFrom` (opt-in, off by default) lets a bar with MANY tabs wrap onto a
// second row from that breakpoint up instead of scrolling: Compliance
// management's 11 tabs need ~1260px, so even a 1024 tablet hid three behind
// the fade (farm audit AD-06 asks a tablet to show all 11). Below the
// breakpoint it scrolls with fades as usual, and so does a phone on its side
// (`short:`, 844×390 is past md): a second sticky row of tabs would cost it
// 44 of its 390px. Every other caller is unchanged.
import { useEffect, useLayoutEffect, useRef } from 'react'
import clsx from 'clsx'
import type { LucideIcon } from 'lucide-react'
import { fadeInAfter } from '@/lib/motion'
import { useEdgeFades } from '@/hooks/useEdgeFades'

export interface ModuleTab<K extends string> {
  key:   K
  label: string
  icon:  LucideIcon
  /** Optional attention dot after the label (e.g. Config → Setup issues). */
  dot?:  'red' | 'amber'
  /** Optional live count after the label, e.g. QC's "Full Box (3)". A number
   *  renders only when > 0 — a zero would read as a claim that the tab is empty
   *  when the count is merely still loading. A STRING renders as given: pass
   *  `countOrDash(q, n)` when the caller already knows read-state and wants "(—)"
   *  over a failed read and "(0)" over a real zero (Consignments). The button is
   *  whitespace-nowrap so the suffix can never wrap off its label. */
  count?: number | string
  /** Optional shorter label for a phone (below `sm`). A tab bar where ANY tab
   *  has one drops its icons, tightens its padding and shares the width
   *  equally on a phone, so the tabs fit one screen instead of scrolling
   *  (Inspection UF-09). The full label stays the accessible name. */
  shortLabel?: string
}

/** The accessible name when a phone shows the short label: the full label plus its count. */
function fullName(t: { label: string; shortLabel?: string; count?: number | string }): string | undefined {
  if (!t.shortLabel) return undefined
  return t.count === undefined || t.count === 0 ? t.label : `${t.label} (${t.count})`
}

function TabLabel({ tab }: { tab: { label: string; shortLabel?: string } }) {
  if (!tab.shortLabel) return <> {tab.label}</>
  return <><span className="max-sm:hidden">{tab.label}</span><span className="sm:hidden">{tab.shortLabel}</span></>
}

const DOT_CLASS = { red: 'bg-red-500', amber: 'bg-amber-400' } as const
// `!`: Tailwind emits the screen variants AFTER the custom `short:` one, so a
// plain short:flex-nowrap loses to md:flex-wrap at 844×390.
const WRAP_CLASS = { md: 'md:flex-wrap short:!flex-nowrap', lg: 'lg:flex-wrap short:!flex-nowrap' } as const

export default function ModuleTabBar<K extends string>({ tabs, value, onChange, wrapFrom }: {
  tabs:     ReadonlyArray<ModuleTab<K>>
  value:    K
  onChange: (key: K) => void
  /** Wrap onto more rows from this breakpoint up instead of scrolling. */
  wrapFrom?: keyof typeof WRAP_CLASS
}) {
  const strip = useRef<HTMLDivElement>(null)
  const fade = useEdgeFades(strip, tabs.length)
  const root = useRef<HTMLDivElement>(null)
  const compact = tabs.some(t => t.shortLabel)

  // Fade the new tab's content in. The bar does not own its panel (25 callers
  // render it themselves), so it fades whatever FOLLOWS it in the page — see
  // fadeInAfter. A layout effect: after the new panel is in the DOM, before
  // paint. Skips the first run so opening a page does not fade twice.
  const mounted = useRef(false)
  useLayoutEffect(() => {
    if (!mounted.current) { mounted.current = true; return }
    fadeInAfter(root.current)
  }, [value])

  // Keep the active tab on screen. The strip is `relative`, so a tab's
  // offsetLeft is measured from it and does not move as it scrolls.
  useEffect(() => {
    const el = strip.current
    const tab = el?.querySelector<HTMLElement>('[aria-selected="true"]')
    if (!el || !tab) return
    const left = tab.offsetLeft
    const right = left + tab.offsetWidth
    if (left < el.scrollLeft) el.scrollLeft = Math.max(0, left - 24)
    else if (right > el.scrollLeft + el.clientWidth) el.scrollLeft = right - el.clientWidth + 24
  }, [value, tabs.length])

  return (
    <div ref={root} className="relative min-w-0">
      <div ref={strip} role="tablist" className={clsx('relative flex overflow-x-auto scrollbar-hide', wrapFrom && WRAP_CLASS[wrapFrom])}>
        {tabs.map(t => {
          const Icon = t.icon
          const active = value === t.key
          return (
            <button key={t.key} type="button" role="tab" aria-selected={active}
              aria-label={fullName(t)}
              onClick={() => onChange(t.key)}
              className={clsx(
                'shrink-0 flex items-center gap-1.5 min-h-[44px] py-2.5 px-4 text-sm font-semibold pressable whitespace-nowrap',
                compact && 'max-sm:flex-1 max-sm:justify-center max-sm:gap-1 max-sm:px-1.5 max-sm:text-[13px]',
                active
                  ? 'text-white light:text-fg border-b-2 border-green-500'
                  : 'text-muted hover:text-fg-secondary'
              )}>
              <Icon size={14} className={clsx(compact && 'max-sm:hidden')} />
              <TabLabel tab={t} />
              {(typeof t.count === 'string' || (t.count ?? 0) > 0) && <span className="text-xs text-gray-400">({t.count})</span>}
              {t.dot && <span className={clsx('w-1.5 h-1.5 rounded-full', DOT_CLASS[t.dot])} />}
            </button>
          )
        })}
      </div>
      {fade.left && (
        <div aria-hidden className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-canvas to-transparent" />
      )}
      {fade.right && (
        <div aria-hidden className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-canvas to-transparent" />
      )}
    </div>
  )
}
